import { LoadingButton } from '@atlaskit/button';
import ModalDialog, {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '@atlaskit/modal-dialog';
import React, { type FunctionComponent, useEffect, useState, useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { overrideModalBlanketStyles } from '../blanket';
import useDialog from '../hooks/useDialog';
import { messages } from '../messages';
import { type ConfirmDialogProps } from '../types';

const ConfirmDialog: FunctionComponent<ConfirmDialogProps> = ({
  heading,
  body,
  yesLabel,
  noLabel,
  appearance,
  onClose,
  isBlanketHidden,
  width = 'small',
}: ConfirmDialogProps) => {
  const [isLoading, setIsLoading] = useState<1 | 2>();
  const { hideDialog } = useDialog();

  const handleClose = useCallback(
    async (result: boolean) => {
      setIsLoading(result ? 1 : 2);

      await onClose(result);

      hideDialog();
    },
    [hideDialog, onClose]
  );

  const handleYesClick = useCallback(async () => {
    await handleClose(true);
  }, [handleClose]);

  const handleNoClick = useCallback(async () => {
    await handleClose(false);
  }, [handleClose]);

  const testId = 'confirm-dialog';

  useEffect(() => {
    overrideModalBlanketStyles(testId);
  }, []);

  const { formatMessage } = useIntl();
  const showNoLabel = !!noLabel || (!body && !yesLabel && !noLabel);

  return (
    <ModalDialog
      width={width}
      autoFocus
      onClose={handleNoClick}
      isBlanketHidden={isBlanketHidden}
      testId={testId}
    >
      <ModalHeader hasCloseButton>
        <ModalTitle appearance={appearance ?? 'warning'}>
          {heading ?? formatMessage(messages['discard-changes-dialog.heading'])}
        </ModalTitle>
      </ModalHeader>
      <ModalBody>
        {body ?? formatMessage(messages['discard-changes-dialog.warning-body'])}
      </ModalBody>
      <ModalFooter>
        {showNoLabel && (
          <LoadingButton
            appearance="subtle"
            onClick={handleNoClick}
            isLoading={isLoading === 2}
            isDisabled={isLoading === 1}
          >
            {noLabel ?? formatMessage(messages['discard-changes-dialog.no-button-text'])}
          </LoadingButton>
        )}
        <LoadingButton
          appearance={appearance ?? 'primary'}
          type="submit"
          onClick={handleYesClick}
          isLoading={isLoading === 1}
          isDisabled={isLoading === 2}
        >
          {yesLabel ?? formatMessage(messages['discard-changes-dialog.yes-button-text'])}
        </LoadingButton>
      </ModalFooter>
    </ModalDialog>
  );
};

export default ConfirmDialog;
